import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageLink, PageTitle } from '../../layout/core';
import {
	useDisputesContext
} from '../../store/disputes/disputes-context';
import {
	DisputeQueuesFilterSection,
	IDisputeQueuesFilters,
} from '../../components/disputes/DisputeQueuesFilterSection';
import {
	fetchDisputeQueues,
	setDisputeQueueRetry,
	setDisputesQueueDataToDefault,
} from '../../store/disputes/actions/dispute-queues';

import'../../components/disputes/disputes_queues/DisputesQueue.scss';
import {
	DisputeQueuesTable
} from '../../components/disputes/disputes_queues/disputes_queue_details/DisputeQueuesTable';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import {
	NewQueueModal
} from '../../components/disputes/disputes_queues/disputes_queue_details/NewQueueModal';
import { DeleteTab } from '../../components/disputes/disputes_queues/DeleteTab';
import { fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useLocation } from 'react-router';
import { parseSearchParams } from '../../utils/utilities';

const unMatchedDisputesBreadCrumbs: Array<PageLink> = [
	{
		title: 'MENU.DISPUTES',
		path: '/disputes/all',
		isSeparator: false,
		isActive: false
	},
	{
		title: '',
		path: '',
		isSeparator: true,
		isActive: false
	}
];

export const DisputesQueuePage: FC = () => {
	const { t } = useTranslation();
	const { search } = useLocation();

	const { dispatch: merchantDispatch } = useMerchants();

	const { dispatch } = useDisputesContext();

	const [timezone, setTimezone] = useState<string>('UTC');
	const [from, setFrom] = useState<Date | string>(null);
	const [to, setTo] = useState<Date | string>(null);
	const [isFinished, setIsFinished] = useState<string>('false');
	const [partnerId, setPartnerId] = useState<string>('');
	const [query, setQuery] = useState<string>('');


	const [retryModalState, setRetryModalState] = useState({
		isOpen: false,
		id: ''
	});

	const [deleteModalState, setDeleteModalState] = useState({
		isOpen: false,
		id: ''
	});
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const filtersFromUrl: IDisputeQueuesFilters = parseSearchParams(search) || {};

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeDeleteModal = (): void => setDeleteModalState({ isOpen: false, id: '' });
	const closeRetryModal = (): void => setRetryModalState({ isOpen: false, id: '' });

	useEffect(() => {
		void fetchWhiteListedPartnersList(merchantDispatch);
	}, []);

	useEffect(() => {
		return () => {
			setDisputesQueueDataToDefault(dispatch);
		};
	}, []);

	const handleErrorRetry = (): void => {
		setModalState({
      isOpen: true,
      modalTitle: 'Retry',
      modalText: 'Failed retry records',
      onClose: closeModal,
      onConfirm: closeModal,
      backdrop: 'static'
    });
	};

	const handleConfirmRetry = async (): Promise<void> => {
		try {
			await setDisputeQueueRetry(retryModalState?.id);
			setTimeout(async () => {
				await fetchDisputeQueues({ dispatch, searchParams: filtersFromUrl });
				closeRetryModal();
			}, 6000);

		} catch (e) {
			closeRetryModal();
			handleErrorRetry();
			console.log('handleConfirmRetry', e.response);
		}
	};

	const openRetryModal = (queueId: string): void => {
		setRetryModalState({
			isOpen: true,
			id: queueId
		});
	};

	const openDeleteModal = (isOpen, id): void => setDeleteModalState({ isOpen, id });

	const openNewQueueModal = (queueId?: string): void => {
		setModalState({
			isOpen: true,
			modalTitle: queueId ? 'Edit new queue' : 'Add new queue',
			minHeight: 300,
			contentClassName: 'd-block',
			backdrop: 'static',
			content: <NewQueueModal closeModal={closeModal} queueId={queueId} />
		});
	};

	const listener = async (searchParams: IDisputeQueuesFilters): Promise<void> => {
		let localSearchParams: IDisputeQueuesFilters;

		if (Object.keys(searchParams).length) {
			localSearchParams = { ...searchParams };

			setFrom(searchParams?.from || null);
			setTo(searchParams?.to || null);
			setPartnerId(searchParams?.partnerId || '');
			setTimezone(searchParams?.timezone || 'UTC');
			setQuery(searchParams?.query || '');

			if (searchParams?.isFinished && searchParams?.isFinished === 'null') {
				setIsFinished('');
			} else {
				setIsFinished(searchParams?.isFinished || 'false');
			}
		} else {
			localSearchParams = {
				isFinished,
				timezone,
				pageSize: '10',
				pageNumber: '1'
			};

		}

		await fetchDisputeQueues({ dispatch, searchParams: localSearchParams });
	};


	return (
		<>
			<PageTitle breadcrumbs={unMatchedDisputesBreadCrumbs}>{t('MENU.DISPUTES_QUEUE')}</PageTitle>

			<DisputeQueuesFilterSection
				openAddQueueModal={(): void => openNewQueueModal()}
				partnerId={partnerId}
				setPartnerId={setPartnerId}
				timezone={timezone}
				setTimezone={setTimezone}
				from={from}
				setFrom={setFrom}
				to={to}
				setTo={setTo}
				isFinished={isFinished}
				setIsFinished={setIsFinished}
				query={query}
				setQuery={setQuery}
			/>
			<DisputeQueuesTable
				listener={listener}
				openRetryModal={openRetryModal}
				openDeleteModal={openDeleteModal}
				openEditQueueModal={openNewQueueModal}
			/>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={deleteModalState.isOpen}
				onClose={closeDeleteModal}
				modalTitle="Delete queue"
				content={<DeleteTab closeModal={closeDeleteModal} id={deleteModalState.id}/>}
				minHeight={200}
			/>

			<ModalWindow
				isOpen={retryModalState?.isOpen}
				modalTitle="Retry"
				modalText="Do you want to retry failed records?"
				onClose={closeRetryModal}
				onCancel={closeRetryModal}
				onConfirm={handleConfirmRetry}
			/>
		</>
	);
};
